// src/misc/america250/America250EventsPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { fetchSheetRows } from "../googleSheets/googleSheetClient";
import { AMERICA250_SHEET } from "./america250Config";

import America250Logo from "../../images/America250.png";
import TriviaAcrossAmericaLogo from "../../images/Trivia_Across_America.png";
import KeepingDreamLogo from "../../images/Keeping_Dream.png";
import RockAndRollLogo from "../../images/Rock_And_Roll.png";
import RosieRiveterLogo from "../../images/rosie-riveter.jpg";
import FifeAndDrumsLogo from "../../images/Fife_and_Drums.png";
import Kindness250Logo from "../../images/250_Kindness.png";
import DroneShowLogo from "../../images/Drone_Show.png";

import { LOCATION_MAP_LINKS } from "./locationLinks";
import { ORGANIZER_LINKS } from "./organizerLinks";
import { safeStr, getField, normalizeStatus } from "../utils/sheetRowUtils";
import { parseLocalDateTime, formatWhenRange } from "../utils/calendarUtils";
import { CalendarDropdown } from "./CalendarDropdown";

// ✅ One form for all events (users will type event name/time themselves)
const VOLUNTEER_FORM_URL = "https://forms.gle/rELqJAhqMpdg5jQk9";

export function America250EventsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchSheetRows(AMERICA250_SHEET);

        const normalized = data
          .map((r, idx) => {
            const status = normalizeStatus(r);
            const title = safeStr(getField(r, "Title"));
            const start = safeStr(getField(r, "Start"));

            return {
              __id: `${idx}-${title}-${start}`,
              ...r,
              __status: status,
            };
          })
          .filter((r) => {
            const title = safeStr(getField(r, "Title"));
            const organizer = safeStr(getField(r, "Organizer"));
            const start = safeStr(getField(r, "Start"));
            return title || organizer || start;
          })
          .filter((r) => safeStr(r.__status).toLowerCase() === "approved");

        if (mounted) setRows(normalized);
      } catch (e) {
        console.error(e);
        if (mounted) setError(e?.message || "Failed to load events");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // 1) filter by search query
    const matching = rows.filter((r) => {
      if (!q) return true;

      const hay = [
        getField(r, "Organizer"),
        getField(r, "Title"),
        getField(r, "Description"),
        getField(r, "Location"),
        getField(r, "Start"),
        getField(r, "End"),
      ]
        .map(safeStr)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });

    // 2) sort by event start date (soonest / earliest first)
    //    If parsing fails, push the item to the bottom.
    return matching
      .map((r) => {
        const start = safeStr(getField(r, "Start"));
        const startTime = safeStr(getField(r, "Start Time"));
        const startDate = parseLocalDateTime(start, startTime);

        return {
          ...r,
          __sortMs: startDate ? startDate.getTime() : Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => a.__sortMs - b.__sortMs);
  }, [rows, query]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3">
        <img
          src={America250Logo}
          alt="America 250 logo"
          className="h-12 w-auto object-contain"
        />
        <h1 className="text-2xl font-semibold">
          Glastonbury America 250 Events
        </h1>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title, organizer, location, description…"
          className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
        />
      </div>

      {/* States */}
      {loading && (
        <div className="mt-4 text-sm text-gray-700">Loading events…</div>
      )}
      {error && (
        <div className="mt-4 text-sm text-red-700">
          {error}
          <div className="text-xs text-gray-600 mt-1">
            If this is a permissions/CORS issue, the sheet may need “Publish to
            the web” enabled.
          </div>
        </div>
      )}

      {/* List */}
      {!loading && !error && (
        <div className="mt-5 grid grid-cols-1 gap-3">
          {filtered.map((r) => {
            const title = safeStr(getField(r, "Title"));
            const organizer = safeStr(getField(r, "Organizer"));
            const start = safeStr(getField(r, "Start"));
            const startTime = safeStr(getField(r, "Start Time"));
            const endTime = safeStr(getField(r, "End Time"));
            const location = safeStr(getField(r, "Location"));
            const desc = safeStr(getField(r, "Description"));

            const startDate = parseLocalDateTime(start, startTime);
            const endDate = endTime ? parseLocalDateTime(start, endTime) : null;

            // Replace here to add more images
            const titleLower = title.toLowerCase().trim();

            const isTrivia = titleLower.includes("trivia");
            const isKindness250 = titleLower.includes("250 acts of kindness");
            const isKeepingDream = titleLower.includes("keeping the dream");
            const isDroneShow = titleLower.includes("drone show");
            const isBirthOfRockAndRoll =
              titleLower === "the birth of rock & roll";
            const isRosieRiveter = titleLower.includes("rosie the riveter");
            const isFifeAndDrums = titleLower.includes(
              "colchester continental fife and drum",
            );

            const eventLogo = isTrivia
              ? TriviaAcrossAmericaLogo
              : isKindness250
                ? Kindness250Logo
                : isKeepingDream
                  ? KeepingDreamLogo
                  : isBirthOfRockAndRoll
                    ? RockAndRollLogo
                    : isRosieRiveter
                      ? RosieRiveterLogo
                      : isDroneShow
                        ? DroneShowLogo
                        : isFifeAndDrums
                          ? FifeAndDrumsLogo
                          : America250Logo;

            const eventLogoAlt = isTrivia
              ? "Trivia Across America"
              : isKindness250
                ? "250 Acts of Kindness"
                : isKeepingDream
                  ? "Keeping the Dream Alive"
                  : isBirthOfRockAndRoll
                    ? "The Birth of Rock & Roll"
                    : isDroneShow
                      ? "America 250 Drone Show"
                      : isRosieRiveter
                        ? "Rosie the Riveter"
                        : isFifeAndDrums
                          ? "Colchester Continental Fife and Drum Corps"
                          : "America 250";
            // Replace here to add more images

            return (
              <details
                key={r.__id}
                className="border border-gray-300 rounded bg-white"
              >
                <summary className="cursor-pointer px-4 py-3 list-none marker:content-none [&::-webkit-details-marker]:hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_176px] gap-4 items-start">
                    {/* LEFT: Desktop logo */}
                    <div className="hidden sm:block">
                      <div className="flex items-center justify-center">
                        <img
                          src={eventLogo}
                          alt={eventLogoAlt}
                          className="h-28 w-full object-contain"
                        />
                      </div>
                    </div>

                    {/* MIDDLE: Title + meta */}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-base">
                          {title || "(Untitled Event)"}
                        </span>
                      </div>

                      <div className="text-sm text-gray-700 mt-2 space-y-1">
                        {organizer && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 w-20 shrink-0">
                              Organizer:
                            </span>

                            {ORGANIZER_LINKS[organizer] ? (
                              <a
                                href={ORGANIZER_LINKS[organizer]}
                                target="_blank"
                                rel="noreferrer"
                                className="min-w-0 text-blue-700 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {organizer}
                              </a>
                            ) : (
                              <span className="min-w-0">{organizer}</span>
                            )}
                          </div>
                        )}
                        {(startDate || start || startTime) && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 w-20 shrink-0">
                              When:
                            </span>
                            <span className="min-w-0">
                              {startDate
                                ? formatWhenRange(startDate, endDate)
                                : `${start}${startTime ? ` • ${startTime}` : ""}${endTime ? ` – ${endTime}` : ""}`}
                            </span>
                          </div>
                        )}
                        {location && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 w-20 shrink-0">
                              Where:
                            </span>

                            {LOCATION_MAP_LINKS[location] ? (
                              <a
                                href={LOCATION_MAP_LINKS[location]}
                                target="_blank"
                                rel="noreferrer"
                                className="min-w-0 text-blue-700 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {location}
                              </a>
                            ) : (
                              <span className="min-w-0">{location}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT: Actions */}
                    <div className="w-full sm:w-44 flex flex-col gap-2 sm:justify-start">
                      <a
                        href={VOLUNTEER_FORM_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-blue-600 text-blue-700 rounded px-3 py-2 text-sm hover:bg-blue-50 text-center"
                        title="Volunteer for this event"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Volunteer
                      </a>

                      <div onClick={(e) => e.stopPropagation()}>
                        <CalendarDropdown
                          title={title}
                          description={desc}
                          location={location}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      </div>
                    </div>
                  </div>
                </summary>

                <div className="px-4 pb-4 pt-1 text-sm">
                  {desc ? (
                    <div className="whitespace-pre-wrap text-gray-800">
                      {desc}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">No description.</div>
                  )}

                  {/* Mobile logo BELOW the description */}
                  <div className="mt-3 sm:hidden flex justify-center">
                    <img
                      src={eventLogo}
                      alt={eventLogoAlt}
                      className="h-48 w-auto object-contain"
                    />
                  </div>
                </div>
              </details>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-sm text-gray-600 mt-3">
              No approved events match the current search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
